import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Link as MuiLink,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { linksApi } from '../services/api';
import { UsefulLink, UsefulLinkCreate, UsefulLinkUpdate, CategoryStats } from '../types';
import { useAuth } from '../context/AuthContext';

const VALID_CATEGORIES = ['Payroll', 'Training', 'HR', 'Policies', 'Tools', 'Other'];

const Links: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isManager = user?.role === 'manager';

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<UsefulLink | null>(null);
  const [formData, setFormData] = useState<UsefulLinkCreate>({
    title: '',
    url: '',
    description: '',
    category: 'Other',
    icon_url: '',
    display_order: 0,
    is_active: true,
    open_in_new_tab: true,
  });

  // Queries
  const { data: linksData, isLoading: linksLoading, error: linksError } = useQuery({
    queryKey: ['links', searchTerm, selectedCategory, isManager],
    queryFn: () => linksApi.getLinks({
      search: searchTerm || undefined,
      category: selectedCategory || undefined,
      is_active: isManager ? undefined : true,
    }),
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['linkCategories'],
    queryFn: () => linksApi.getCategories(),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: UsefulLinkCreate) => linksApi.createLink(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links'] });
      queryClient.invalidateQueries({ queryKey: ['linkCategories'] });
      setCreateDialogOpen(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UsefulLinkUpdate }) =>
      linksApi.updateLink(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links'] });
      setEditDialogOpen(false);
      setSelectedLink(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => linksApi.deleteLink(id, false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links'] });
      queryClient.invalidateQueries({ queryKey: ['linkCategories'] });
      setDeleteDialogOpen(false);
      setSelectedLink(null);
    },
  });

  const clickMutation = useMutation({
    mutationFn: (id: number) => linksApi.trackClick(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['links'] });
    },
  });

  // Handlers
  const resetForm = () => {
    setFormData({
      title: '',
      url: '',
      description: '',
      category: 'Other',
      icon_url: '',
      display_order: 0,
      is_active: true,
      open_in_new_tab: true,
    });
  };

  const handleCreateClick = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  const handleEditClick = (link: UsefulLink) => {
    setSelectedLink(link);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (link: UsefulLink) => {
    setSelectedLink(link);
    setDeleteDialogOpen(true);
  };

  const handleCreateSubmit = () => {
    createMutation.mutate(formData);
  };

  const handleUpdateSubmit = () => {
    if (!selectedLink) return;

    const updateData: UsefulLinkUpdate = {
      title: selectedLink.title,
      url: selectedLink.url,
      description: selectedLink.description || undefined,
      category: selectedLink.category,
      icon_url: selectedLink.icon_url || undefined,
      display_order: selectedLink.display_order,
      is_active: selectedLink.is_active,
      open_in_new_tab: selectedLink.open_in_new_tab,
    };

    updateMutation.mutate({ id: selectedLink.id, data: updateData });
  };

  const handleDeleteConfirm = () => {
    if (!selectedLink) return;
    deleteMutation.mutate(selectedLink.id);
  };

  const handleLinkClick = (link: UsefulLink) => {
    clickMutation.mutate(link.id);
    if (link.open_in_new_tab) {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = link.url;
    }
  };

  // Group links by category
  const groupedLinks = React.useMemo(() => {
    if (!linksData?.links) return {};

    const groups: Record<string, UsefulLink[]> = {};
    linksData.links.forEach((link: UsefulLink) => {
      if (!groups[link.category]) {
        groups[link.category] = [];
      }
      groups[link.category].push(link);
    });

    // Sort links within each category by display_order
    Object.keys(groups).forEach(category => {
      groups[category].sort((a, b) => a.display_order - b.display_order);
    });

    return groups;
  }, [linksData]);

  // Sort categories to show them in the predefined order
  const sortedCategories = React.useMemo(() => {
    const categories = Object.keys(groupedLinks);
    return VALID_CATEGORIES.filter(cat => categories.includes(cat));
  }, [groupedLinks]);

  if (linksError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          Failed to load links. Please try again later.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Useful Links
        </Typography>
        {isManager && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
          >
            Add Link
          </Button>
        )}
      </Box>

      {/* Search and Filter */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          placeholder="Search links..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            label="Category"
          >
            <MenuItem value="">All Categories</MenuItem>
            {VALID_CATEGORIES.map(cat => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Category Statistics */}
      {categoriesData?.categories && (
        <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {categoriesData.categories.map((cat: CategoryStats) => (
            <Chip
              key={cat.name}
              label={`${cat.name} (${isManager ? cat.count : cat.active_count})`}
              onClick={() => setSelectedCategory(selectedCategory === cat.name ? '' : cat.name)}
              color={selectedCategory === cat.name ? 'primary' : 'default'}
            />
          ))}
        </Box>
      )}

      {/* Links grouped by category */}
      {linksLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : sortedCategories.length === 0 ? (
        <Alert severity="info">
          No links found. {isManager && 'Click "Add Link" to create one.'}
        </Alert>
      ) : (
        sortedCategories.map(category => (
          <Box key={category} sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
              {category}
            </Typography>
            <Grid container spacing={2}>
              {groupedLinks[category]?.map(link => (
                <Grid item xs={12} sm={6} md={4} key={link.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      opacity: link.is_active ? 1 : 0.6,
                      border: link.is_active ? undefined : '1px dashed',
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                        {link.icon_url ? (
                          <img
                            src={link.icon_url}
                            alt=""
                            style={{ width: 24, height: 24, marginRight: 8 }}
                          />
                        ) : (
                          <LinkIcon sx={{ mr: 1, color: 'primary.main' }} />
                        )}
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="div" sx={{ fontSize: '1.1rem' }}>
                            {link.title}
                          </Typography>
                          {!link.is_active && (
                            <Chip label="Inactive" size="small" color="warning" sx={{ mt: 0.5 }} />
                          )}
                        </Box>
                      </Box>
                      {link.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {link.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {link.click_count} {link.click_count === 1 ? 'click' : 'clicks'}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                      <Button
                        size="small"
                        onClick={() => handleLinkClick(link)}
                        endIcon={link.open_in_new_tab ? <OpenInNewIcon /> : undefined}
                      >
                        Open Link
                      </Button>
                      {isManager && (
                        <Box>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(link)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(link)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      )}

      {/* Create Link Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Link</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="URL"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              required
              fullWidth
              placeholder="https://example.com"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                label="Category"
              >
                {VALID_CATEGORIES.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Icon URL (optional)"
              value={formData.icon_url}
              onChange={(e) => setFormData({ ...formData, icon_url: e.target.value })}
              fullWidth
              placeholder="https://example.com/icon.png"
            />
            <TextField
              label="Display Order"
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Open in New Tab</InputLabel>
              <Select
                value={formData.open_in_new_tab ? 'true' : 'false'}
                onChange={(e) => setFormData({ ...formData, open_in_new_tab: e.target.value === 'true' })}
                label="Open in New Tab"
              >
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.is_active ? 'active' : 'inactive'}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateSubmit}
            variant="contained"
            disabled={!formData.title || !formData.url || createMutation.isLoading}
          >
            {createMutation.isLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Link Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Link</DialogTitle>
        <DialogContent>
          {selectedLink && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Title"
                value={selectedLink.title}
                onChange={(e) => setSelectedLink({ ...selectedLink, title: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="URL"
                value={selectedLink.url}
                onChange={(e) => setSelectedLink({ ...selectedLink, url: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Description"
                value={selectedLink.description || ''}
                onChange={(e) => setSelectedLink({ ...selectedLink, description: e.target.value })}
                multiline
                rows={2}
                fullWidth
              />
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedLink.category}
                  onChange={(e) => setSelectedLink({ ...selectedLink, category: e.target.value })}
                  label="Category"
                >
                  {VALID_CATEGORIES.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Icon URL (optional)"
                value={selectedLink.icon_url || ''}
                onChange={(e) => setSelectedLink({ ...selectedLink, icon_url: e.target.value })}
                fullWidth
              />
              <TextField
                label="Display Order"
                type="number"
                value={selectedLink.display_order}
                onChange={(e) => setSelectedLink({ ...selectedLink, display_order: parseInt(e.target.value) || 0 })}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Open in New Tab</InputLabel>
                <Select
                  value={selectedLink.open_in_new_tab ? 'true' : 'false'}
                  onChange={(e) => setSelectedLink({ ...selectedLink, open_in_new_tab: e.target.value === 'true' })}
                  label="Open in New Tab"
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedLink.is_active ? 'active' : 'inactive'}
                  onChange={(e) => setSelectedLink({ ...selectedLink, is_active: e.target.value === 'active' })}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateSubmit}
            variant="contained"
            disabled={!selectedLink?.title || !selectedLink?.url || updateMutation.isLoading}
          >
            {updateMutation.isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the link "{selectedLink?.title}"? This will deactivate the link.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Links;
